import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import BrandLogo from "../components/BrandLogo";
import GuestLeadModal from "../components/GuestLeadModal";
import AuthModal from "../components/AuthModal";
import { ShieldCheck, Lock, EyeOff, Award, Sparkles, BookOpen, Inbox, Store, Download, UserCheck, CreditCard, Zap, Users, Briefcase, Heart, ArrowRight } from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/33417236/pexels-photo-33417236.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900";

const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.vaidikshadi.app";

const PlayBadge = () => (
  <a
    href={PLAY_STORE_URL}
    target="_blank"
    rel="noopener noreferrer"
    data-testid="play-store-badge"
    aria-label="Get it on Google Play"
    className="flex items-center gap-4 px-8 py-6 select-none transition-transform hover:scale-[1.02]"
    style={{
      background: "#0F0F0F",
      color: "#FFFFFF",
      border: "1px solid #2A2A2A",
      borderRadius: "12px",
      minWidth: "380px",
      textDecoration: "none",
    }}
  >
    <svg width="52" height="56" viewBox="0 0 22 24" fill="none" aria-hidden>
      <path d="M1.5 1.2v21.6c0 .8.4 1.5 1.1 1.8L14.3 12 2.6.6C2 .9 1.5 1.4 1.5 1.2z" fill="url(#g1)"/>
      <path d="M18.7 8.3l-3.4-2L11.7 10l3.6 3.7 3.4-2c1-.6 1-2.2 0-2.8l-.2.5z" fill="#FFC107"/>
      <path d="M2.6 23.4c.3.2.7.2 1.1 0l13.5-7.8L14.3 12 2.6 23.4z" fill="#EA4335"/>
      <path d="M2.6.6L14.3 12l2.9-3.6L3.7.6c-.4-.2-.8-.2-1.1 0z" fill="#34A853"/>
      <defs>
        <linearGradient id="g1" x1="0" y1="0" x2="14" y2="12" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#00C6FF"/>
          <stop offset="1" stopColor="#0072FF"/>
        </linearGradient>
      </defs>
    </svg>
    <div className="text-left leading-tight">
      <div style={{ fontSize: "13px", letterSpacing: "0.08em", opacity: 0.85 }}>GET IT ON</div>
      <div style={{ fontSize: "28px", fontWeight: 700, letterSpacing: "0.01em" }}>Google Play</div>
    </div>
  </a>
);

const AppleBadge = () => (
  <div className="flex flex-col items-start gap-2">
    <div
      data-testid="app-store-badge"
      aria-disabled="true"
      title="Coming soon"
      className="flex items-center gap-3 px-5 py-3.5 select-none"
      style={{
        background: "#0F0F0F",
        color: "#FFFFFF",
        border: "1px solid #2A2A2A",
        borderRadius: "10px",
        minWidth: "190px",
        cursor: "not-allowed",
        opacity: 0.55,
        pointerEvents: "none",
      }}
    >
      <svg width="28" height="30" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M17.05 12.46c-.02-2.7 2.2-3.99 2.3-4.05-1.25-1.84-3.2-2.09-3.9-2.12-1.66-.17-3.24.97-4.09.97-.85 0-2.15-.95-3.54-.92-1.82.03-3.5 1.06-4.43 2.69-1.89 3.28-.48 8.12 1.36 10.78.9 1.3 1.97 2.76 3.36 2.7 1.35-.05 1.86-.87 3.5-.87 1.63 0 2.1.87 3.53.84 1.46-.02 2.39-1.32 3.28-2.62 1.04-1.5 1.47-2.95 1.5-3.03-.03-.01-2.87-1.1-2.9-4.37zM14.4 4.6c.74-.9 1.24-2.14 1.1-3.39-1.07.04-2.36.71-3.13 1.6-.69.79-1.29 2.06-1.13 3.27 1.2.09 2.43-.6 3.16-1.48z"/>
      </svg>
      <div className="text-left leading-tight">
        <div style={{ fontSize: "10px", letterSpacing: "0.08em", opacity: 0.85 }}>DOWNLOAD ON THE</div>
        <div style={{ fontSize: "17px", fontWeight: 600, letterSpacing: "0.01em" }}>App Store</div>
      </div>
    </div>
    <p data-testid="app-store-note"
      className="text-[11px] tracking-widest uppercase"
      style={{ color: "var(--ash)", marginLeft: "4px" }}>
      Coming Soon
    </p>
  </div>
);

const TrustChip = ({ icon: Icon, text }) => (
  <div className="flex items-center gap-2 px-3 py-2.5 text-xs tracking-wide"
    style={{ background: "rgba(200, 162, 74, 0.10)", color: "var(--ink)", border: "1px solid rgba(200, 162, 74, 0.35)" }}>
    <Icon size={14} style={{ color: "var(--crimson)" }}/>
    <span style={{ fontWeight: 500 }}>{text}</span>
  </div>
);

const ComplianceBlock = ({ idx, eyebrow, title, children, testid }) => (
  <div data-testid={testid} className="h-full">
    <div className="eyebrow">{`${idx} · ${eyebrow}`}</div>
    <h3 className="font-serif text-2xl md:text-3xl mt-2" style={{ color: "var(--ink)" }}>{title}</h3>
    <div className="mt-4 space-y-3 text-sm md:text-[15px] leading-relaxed" style={{ color: "var(--ash)" }}>
      {children}
    </div>
  </div>
);

export default function Landing() {
  const nav = useNavigate();
  const [modal, setModal] = useState(null); // "guest" | "wizard" | null
  const openGuest = () => setModal("guest");
  const openVendor = () => nav("/vendor/dashboard");
  const openWizard = () => setModal("wizard");
  const closeModal = () => setModal(null);

  return (
    <div data-testid="landing-page" className="min-h-screen w-full flex flex-col" style={{ background: "var(--ivory)" }}>
      {/* Top minimal brand strip */}
      <header className="px-6 md:px-10 pt-8 pb-2 flex items-center justify-center md:justify-start">
        <BrandLogo size="md" />
      </header>

      {/* HERO — side-by-side on desktop, stacked on mobile */}
      <section className="px-6 md:px-10 mt-6 md:mt-10">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          {/* Image */}
          <div className="relative lg:col-span-7 order-1">
            <div className="relative aspect-[3/4] sm:aspect-[4/3] lg:aspect-[4/5] overflow-hidden border" style={{ borderColor: "var(--gold)" }}>
              <img src={HERO_IMG} alt="Sacred mandap" className="w-full h-full object-cover"/>
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 30%, rgba(26,26,26,0.75) 100%)" }}/>
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10 text-white lg:hidden">
                <div className="eyebrow flex items-center gap-2" style={{ color: "var(--gold)" }}>
                  <Sparkles size={12}/> A Sacred Resolve
                </div>
                <h1 className="font-serif text-3xl sm:text-4xl mt-3 leading-tight" style={{ color: "#FDFCF8" }}>
                  The matrimony app rooted in <em className="serif-italic" style={{ color: "var(--gold)" }}>Vedic tradition.</em>
                </h1>
              </div>
            </div>
          </div>

          {/* Text */}
          <div className="lg:col-span-5 order-2">
            <div className="hidden lg:block">
              <div className="eyebrow flex items-center gap-2" style={{ color: "var(--saffron)" }}>
                <Sparkles size={12}/> A Sacred Resolve
              </div>
              <h1 className="font-serif text-4xl xl:text-5xl mt-3 leading-tight" style={{ color: "var(--crimson)" }}>
                The matrimony app rooted in <em className="serif-italic" style={{ color: "var(--gold-dark)" }}>Vedic tradition.</em>
              </h1>
            </div>

            <h2 className="font-serif text-3xl md:text-4xl mt-4 leading-tight" style={{ color: "var(--crimson)" }}>
              Vaidik Shadi
            </h2>
            <p className="font-devanagari text-base md:text-lg mt-2" style={{ color: "var(--gold-dark)" }}>वैदिक शादी</p>
            <p className="mt-2 text-xs md:text-sm tracking-wide" style={{ color: "var(--ash)", letterSpacing: "0.02em" }}>
              — An Initiative of Vedika Sanskritik Bandhan (वेदिका सांस्कृतिक बंधन) —
            </p>

            <div className="mt-5 inline-flex items-center gap-2 px-4 py-1.5"
              style={{ background: "var(--bone)", border: "1px solid var(--gold)" }}>
              <ShieldCheck size={14} style={{ color: "var(--leaf)" }}/>
              <span className="text-xs tracking-[0.18em] uppercase font-semibold" style={{ color: "var(--ink)" }}>
                vaidikshadi.in · Trusted Domain
              </span>
            </div>

            <p className="font-devanagari text-sm md:text-base mt-5" style={{ color: "var(--ash)" }}>
              परंपरा और विश्वास का अटूट संगम
            </p>
            <p className="mt-4 text-sm md:text-base leading-relaxed" style={{ color: "var(--ash)" }}>
              The full Vaidik Shadi experience — gotra-aware matching, Aadhaar-verified profiles, Match-Box discretion and the complete wedding-vendor atelier — is available <strong style={{ color: "var(--ink)" }}>on our mobile app</strong>.
            </p>

            {/* Download badges — App Store LEFT (inactive) · Google Play RIGHT (active, larger) */}
            <div data-testid="download-section" className="mt-7 flex flex-col sm:flex-row gap-4 sm:items-center">
              <AppleBadge />
              <PlayBadge />
            </div>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="px-6 md:px-10 mt-14 md:mt-20">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4">
            <TrustChip icon={ShieldCheck} text="Aadhaar Verified"/>
            <TrustChip icon={EyeOff} text="Anti-Screenshot"/>
            <TrustChip icon={Lock} text="No Chat Spam"/>
            <TrustChip icon={Award} text="Trademark Protected"/>
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────────────
          THREE-BUTTON INTERACTION MATRIX (Step 1 UI shells)
          Guest User · Vendor · Register for Marriage
          TEMPORARILY HIDDEN per user request — code preserved for re-enable.
      ───────────────────────────────────────────────────────────────── */}
      <section
        className="px-6 md:px-10 mt-14 md:mt-20"
        data-testid="cta-matrix"
        aria-hidden="true"
        style={{ display: "none" }}
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center">
            <div className="eyebrow">Web Access</div>
            <h2 className="font-serif text-3xl md:text-4xl mt-3 leading-tight" style={{ color: "var(--crimson)" }}>
              Choose Your Path
            </h2>
            <div className="divider-line w-24 mx-auto mt-5"/>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {/* Guest User */}
            <button
              data-testid="cta-guest"
              type="button"
              onClick={openGuest}
              className="editorial-card p-7 md:p-8 h-full text-left flex flex-col group"
            >
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-11 h-11"
                  style={{ background: "rgba(122, 14, 24, 0.08)", border: "1px solid var(--gold)" }}>
                  <Users size={20} style={{ color: "var(--crimson)" }}/>
                </span>
                <div className="eyebrow">Free</div>
              </div>
              <h3 className="font-serif text-xl md:text-2xl mt-5" style={{ color: "var(--ink)" }}>Guest User</h3>
              <p className="mt-3 text-sm md:text-[15px] leading-relaxed flex-1" style={{ color: "var(--ash)" }}>
                Share your mobile, city and PIN — we&apos;ll instantly route your enquiry to a trusted local wedding vendor.
              </p>
              <span className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-widest font-semibold"
                style={{ color: "var(--crimson)" }}>
                Find a Vendor <ArrowRight size={14}/>
              </span>
            </button>

            {/* Vendor */}
            <button
              data-testid="cta-vendor"
              type="button"
              onClick={openVendor}
              className="editorial-card p-7 md:p-8 h-full text-left flex flex-col group"
            >
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-11 h-11"
                  style={{ background: "rgba(122, 14, 24, 0.08)", border: "1px solid var(--gold)" }}>
                  <Briefcase size={20} style={{ color: "var(--crimson)" }}/>
                </span>
                <div className="eyebrow">Business</div>
              </div>
              <h3 className="font-serif text-xl md:text-2xl mt-5" style={{ color: "var(--ink)" }}>Vendor</h3>
              <p className="mt-3 text-sm md:text-[15px] leading-relaxed flex-1" style={{ color: "var(--ash)" }}>
                Wedding service providers — login or register your business, receive verified leads, and manage your credit wallet.
              </p>
              <span className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-widest font-semibold"
                style={{ color: "var(--crimson)" }}>
                Vendor Portal <ArrowRight size={14}/>
              </span>
            </button>

            {/* Register for Marriage */}
            <button
              data-testid="cta-marriage"
              type="button"
              onClick={openWizard}
              className="p-7 md:p-8 h-full text-left flex flex-col group"
              style={{ background: "var(--crimson)", color: "#FDFCF8", border: "1px solid var(--gold)" }}
            >
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-11 h-11"
                  style={{ background: "rgba(253, 252, 248, 0.10)", border: "1px solid var(--gold)" }}>
                  <Heart size={20} style={{ color: "var(--gold)" }}/>
                </span>
                <div className="eyebrow" style={{ color: "var(--gold)" }}>Sacred</div>
              </div>
              <h3 className="font-serif text-xl md:text-2xl mt-5" style={{ color: "#FDFCF8" }}>Register for Marriage</h3>
              <p className="mt-3 text-sm md:text-[15px] leading-relaxed flex-1" style={{ color: "rgba(253,252,248,0.82)" }}>
                Verify via SMS OTP and complete our 6-page onboarding wizard — including gotra-aware preferences and Kundali PDF generation.
              </p>
              <span className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-widest font-semibold"
                style={{ color: "var(--gold)" }}>
                Begin Sacred Resolve <ArrowRight size={14}/>
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* Closing brand block + Titan support email */}
      <section className="px-6 md:px-10 mt-14 md:mt-20 mb-8 text-center">
        <div className="max-w-3xl mx-auto">
          <p className="font-serif text-lg md:text-2xl leading-relaxed serif-italic" style={{ color: "var(--ink)" }}>
            &ldquo;A name registered here is a vow to a more meaningful search — a sacred resolve, not a casual click.&rdquo;
          </p>
          <div className="divider-line my-6 w-24 mx-auto"/>
          <a
            href="mailto:support@vaidikshadi.in"
            data-testid="support-mailto"
            className="inline-flex items-center gap-2 text-sm md:text-base tracking-wide"
            style={{ color: "var(--crimson)" }}
          >
            ✉ support@vaidikshadi.in
          </a>
          <p className="mt-3 text-[10px] tracking-widest uppercase" style={{ color: "var(--ash)" }}>
            © 2026 Vaidik Shadi / वैदिक शादी. All rights reserved.
          </p>
          <p className="mt-1 text-[10px] tracking-widest uppercase" style={{ color: "var(--gold-dark)" }}>
            Powered by Vedika Sanskritik Bandhan · West Champaran, Bihar
          </p>
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────────────
          OUR SERVICES — what we offer (Razorpay compliance disclosure)
      ───────────────────────────────────────────────────────────────── */}
      <section className="px-6 md:px-10 mt-16 md:mt-24" data-testid="services-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center">
            <div className="eyebrow">01 · Offering</div>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl mt-3 leading-tight" style={{ color: "var(--crimson)" }}>
              Our Services
            </h2>
            <div className="divider-line w-24 mx-auto mt-5"/>
            <p className="mt-6 text-sm md:text-base leading-relaxed max-w-3xl mx-auto" style={{ color: "var(--ash)" }}>
              We provide digital matrimonial matchmaking on Vaidik Shadi (वैदिक शादी), Vedic astrology-based alignment, and wedding vendor connection services
              exclusively through our mobile application. Our premium features include:
            </p>
          </div>

          <div className="mt-10 md:mt-12 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {/* Kundali Milan */}
            <article data-testid="service-kundali" className="editorial-card p-7 md:p-8 h-full flex flex-col">
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-11 h-11"
                  style={{ background: "rgba(122, 14, 24, 0.08)", border: "1px solid var(--gold)" }}>
                  <BookOpen size={20} style={{ color: "var(--crimson)" }}/>
                </span>
                <div className="eyebrow">Vedic</div>
              </div>
              <h3 className="font-serif text-xl md:text-2xl mt-5" style={{ color: "var(--ink)" }}>
                Kundali Milan
              </h3>
              <p className="mt-3 text-sm md:text-[15px] leading-relaxed" style={{ color: "var(--ash)" }}>
                Detailed horoscope matching and Guna Milan reports based on Vedic astrology — generated and delivered inside the app.
              </p>
            </article>

            {/* Match-Box Activation */}
            <article data-testid="service-matchbox" className="editorial-card p-7 md:p-8 h-full flex flex-col">
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-11 h-11"
                  style={{ background: "rgba(122, 14, 24, 0.08)", border: "1px solid var(--gold)" }}>
                  <Inbox size={20} style={{ color: "var(--crimson)" }}/>
                </span>
                <div className="eyebrow">Discreet</div>
              </div>
              <h3 className="font-serif text-xl md:text-2xl mt-5" style={{ color: "var(--ink)" }}>
                Match-Box Activation
              </h3>
              <p className="mt-3 text-sm md:text-[15px] leading-relaxed" style={{ color: "var(--ash)" }}>
                Secure, curated profile access tailored to individual preferences and gotra-aware filters — opened only when both parties consent.
              </p>
            </article>

            {/* Wedding-Vendor Atelier */}
            <article data-testid="service-vendor" className="editorial-card p-7 md:p-8 h-full flex flex-col">
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-11 h-11"
                  style={{ background: "rgba(122, 14, 24, 0.08)", border: "1px solid var(--gold)" }}>
                  <Store size={20} style={{ color: "var(--crimson)" }}/>
                </span>
                <div className="eyebrow">Atelier</div>
              </div>
              <h3 className="font-serif text-xl md:text-2xl mt-5" style={{ color: "var(--ink)" }}>
                Wedding-Vendor Atelier
              </h3>
              <p className="mt-3 text-sm md:text-[15px] leading-relaxed" style={{ color: "var(--ash)" }}>
                A digital directory to explore, discover, and connect with trusted local wedding service providers and vendors.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────────────
          HOW IT WORKS — 4-step user journey
      ───────────────────────────────────────────────────────────────── */}
      <section className="px-6 md:px-10 mt-16 md:mt-24" data-testid="how-it-works-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center">
            <div className="eyebrow">02 · Journey</div>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl mt-3 leading-tight" style={{ color: "var(--crimson)" }}>
              How It Works
            </h2>
            <div className="divider-line w-24 mx-auto mt-5"/>
            <p className="mt-6 text-sm md:text-base leading-relaxed max-w-3xl mx-auto" style={{ color: "var(--ash)" }}>
              Getting started with Vaidik Shadi (वैदिक शादी) is simple and transparent.
            </p>
          </div>

          <ol className="mt-10 md:mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {[
              { n: "01", icon: Download, title: "Download the App",
                body: "Users download our official mobile application Vaidik Shadi (वैदिक शादी) from the Google Play Store." },
              { n: "02", icon: UserCheck, title: "Profile Creation & Verification",
                body: "Users register by submitting genuine identity information and details." },
              { n: "03", icon: CreditCard, title: "Access & Matchmaking",
                body: "Browse profiles and basic details for free. Unlock detailed Kundali Milan reports, premium Match-Boxes, or specialized wedding-vendor details via a secure digital payment on our platform." },
              { n: "04", icon: Zap, title: "Instant Digital Delivery",
                body: "Upon successful payment, the chosen premium features or vendor access are instantly unlocked and credited directly within the user’s mobile app account." },
            ].map(({ n, icon: Icon, title, body }) => (
              <li key={n} data-testid={`step-${n}`} className="relative p-6 md:p-7 h-full flex flex-col"
                style={{ background: "var(--bone)", border: "1px solid var(--line)" }}>
                <div className="flex items-center justify-between">
                  <span className="font-serif text-3xl md:text-4xl leading-none"
                    style={{ color: "var(--crimson)", letterSpacing: "0.02em" }}>{n}</span>
                  <span className="inline-flex items-center justify-center w-10 h-10"
                    style={{ background: "rgba(200, 162, 74, 0.12)", border: "1px solid var(--gold)" }}>
                    <Icon size={18} style={{ color: "var(--crimson)" }}/>
                  </span>
                </div>
                <div className="divider-line my-5"/>
                <h3 className="font-serif text-lg md:text-xl" style={{ color: "var(--ink)" }}>{title}</h3>
                <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--ash)" }}>{body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────────────
          RAZORPAY COMPLIANCE — Terms, Privacy, Refund, Shipping, Grievance.
      ───────────────────────────────────────────────────────────────── */}
      <section className="px-6 md:px-10 mt-6 md:mt-10" data-testid="compliance-section">
        <div className="max-w-6xl mx-auto">
          <div className="divider-line w-24 mx-auto mb-10"/>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12">
            <ComplianceBlock idx="01" eyebrow="Legal" title="Terms & Conditions" testid="compliance-terms">
              <p><strong style={{ color: "var(--ink)" }}>Eligibility:</strong> This platform is strictly for matrimonial purposes for legally marriageable individuals seeking life partners.</p>
              <p><strong style={{ color: "var(--ink)" }}>Registration &amp; Trust Fee:</strong> Profile creation and registration on our platform <strong style={{ color: "var(--crimson)" }}>Vaidik Shadi / वैदिक शादी</strong> (operated under Vedika Sanskritik Bandhan) via our official domain <a href="https://vaidikshadi.in" style={{ color: "var(--crimson)", textDecoration: "underline" }}>vaidikshadi.in</a> are subject to an applicable registration and trust verification fee for all users.</p>
              <p><strong style={{ color: "var(--ink)" }}>Verification:</strong> Users must provide genuine identity information. Sharing fake profiles or unauthorized commercial activities on this platform will lead to permanent suspension from the network.</p>
            </ComplianceBlock>

            <ComplianceBlock idx="02" eyebrow="Trust" title="Privacy & Data Policy" testid="compliance-privacy">
              <p><strong style={{ color: "var(--ink)" }}>Data Security:</strong> We collect registration details, photos, and contact logs solely to facilitate matchmaking on the <strong style={{ color: "var(--crimson)" }}>Vaidik Shadi / वैदिक शादी</strong> network (operated under Vedika Sanskritik Bandhan). All personal data is securely encrypted.</p>
              <p><strong style={{ color: "var(--ink)" }}>No Third-Party Sharing:</strong> Your personal details and mobile numbers registered on <a href="https://vaidikshadi.in" style={{ color: "var(--crimson)", textDecoration: "underline" }}>vaidikshadi.in</a> are strictly protected and never shared or sold to external marketing agencies.</p>
              <p><strong style={{ color: "var(--ink)" }}>Profile Control:</strong> Users retain full rights to modify, hide, or permanently delete their data through the mobile app interface at any time.</p>

              <div className="mt-4 pt-4" style={{ borderTop: "1px solid var(--line)" }}>
                <p className="font-serif text-base md:text-lg" style={{ color: "var(--ink)" }}>How to Request Account and Data Deletion</p>
                <p className="mt-2">At <strong style={{ color: "var(--crimson)" }}>Vaidik Shadi / वैदिक शादी</strong> (operated under Vedika Sanskritik Bandhan), we respect your privacy and give you full control over your personal data. If you wish to delete your account and associated information, you can do so through the following methods:</p>

                <p className="mt-3"><strong style={{ color: "var(--ink)" }}>Method 1: Within the Mobile Application</strong></p>
                <ul className="mt-2 pl-5 space-y-1" style={{ listStyle: "disc" }}>
                  <li>Open the Mobile App and log into your registered profile.</li>
                  <li>Navigate to the Menu / Profile Settings dashboard section.</li>
                  <li>Click on the <strong style={{ color: "var(--ink)" }}>Delete Account</strong> option.</li>
                  <li>Confirm your choice, and your profile will be removed instantly.</li>
                </ul>

                <p className="mt-3"><strong style={{ color: "var(--ink)" }}>Method 2: Via Website / Email Request</strong></p>
                <ul className="mt-2 pl-5 space-y-1" style={{ listStyle: "disc" }}>
                  <li>If you wish to submit a deletion request through our web platform or cannot access the app, please send an official account deletion request from your registered email address directly to our support team at our updated official email: <a href="mailto:support@vaidikshadi.in" style={{ color: "var(--crimson)", textDecoration: "underline" }} data-testid="privacy-deletion-email">support@vaidikshadi.in</a></li>
                </ul>

                <p className="mt-4"><strong style={{ color: "var(--ink)" }}>What Data Will Be Deleted?</strong></p>
                <p className="mt-2">Upon receiving your deletion request (via App or Email), we will permanently remove and purge the following information from our active servers and databases within 7 business days:</p>
                <ul className="mt-2 pl-5 space-y-1" style={{ listStyle: "disc" }}>
                  <li>Your personal profile details, photos, and contact information.</li>
                  <li>Your account authentication parameters and registration logs.</li>
                </ul>
              </div>

              <div className="mt-4 pt-4" style={{ borderTop: "1px solid var(--line)" }} data-testid="privacy-kyc">
                <p className="font-serif text-base md:text-lg" style={{ color: "var(--ink)" }}>Government KYC &amp; Aadhaar Data Security</p>
                <p className="mt-2">We prioritize the security of your government-issued identifiers. For Aadhaar-based KYC verification, our application integrates exclusively with secure, authorized gateway endpoints. We enforce strict data-masking compliance; under no circumstances do we store, log, or maintain raw Aadhaar numbers, biometric payloads, or unencrypted verification logs on our servers. All identity validation tokens are transmitted using secure end-to-end SSL encryption.</p>
              </div>

              <div className="mt-4 pt-4" style={{ borderTop: "1px solid var(--line)" }} data-testid="privacy-astrology">
                <p className="font-serif text-base md:text-lg" style={{ color: "var(--ink)" }}>Astrology &amp; Birth Details Processing</p>
                <p className="mt-2">To generate accurate personalized horoscopes and reports, our platform securely processes birth parameters (date, time, and location of birth) via sandboxed industry-standard <strong style={{ color: "var(--ink)" }}>Prokerala API</strong> networks. This data is strictly utilized for structural matching and generation of the Detailed Kundli Report and is never shared or distributed to any unauthorized third-party marketing networks.</p>
              </div>
            </ComplianceBlock>

            <ComplianceBlock idx="03" eyebrow="Payments" title="Payment & Refund Terms" testid="compliance-refund">
              <p><strong style={{ color: "var(--ink)" }}>Service Fees:</strong> We only charge fees for specific digital premium features inside the application, which include Kundali Milan (horoscope matching) and Matching Box Opening.</p>
              <p><strong style={{ color: "var(--ink)" }}>Non-Refundable Fees:</strong> All payments made for Kundali Milan reports or Matching Box Opening on the <strong style={{ color: "var(--crimson)" }}>Vaidik Shadi / वैदिक शादी</strong> platform (operated under Vedika Sanskritik Bandhan, <a href="https://vaidikshadi.in" style={{ color: "var(--crimson)", textDecoration: "underline" }}>vaidikshadi.in</a>) are final and strictly non-refundable under any circumstances.</p>
              <p><strong style={{ color: "var(--ink)" }}>No Guarantees:</strong> Paid features grant access to premium matchmaking filters; we do not guarantee an immediate or successful marriage alliance.</p>
              <p className="pt-2" data-testid="refund-failed-tx"><strong style={{ color: "var(--ink)" }}>Failed Transactions and Automated Refunds:</strong> However, in case of a failed transaction where money is deducted from the user&apos;s account but access to the digital service is not granted, the amount will be automatically refunded to the user&apos;s original payment method within 5-7 business days.</p>
            </ComplianceBlock>

            <ComplianceBlock idx="04" eyebrow="Delivery" title="Shipping & Delivery Policy" testid="compliance-shipping">
              <p><strong style={{ color: "var(--ink)" }}>Instant Digital Delivery:</strong> As a digital matchmaking provider, <strong style={{ color: "var(--crimson)" }}>Vaidik Shadi / वैदिक शादी</strong> (operated under Vedika Sanskritik Bandhan) has no physical shipping or logistics involved on <a href="https://vaidikshadi.in" style={{ color: "var(--crimson)", textDecoration: "underline" }}>vaidikshadi.in</a>.</p>
              <p><strong style={{ color: "var(--ink)" }}>Activation Time:</strong> Access to Kundali Milan reports and unlocked Matching Boxes is provisioned instantly and credited to the user&apos;s account immediately upon successful payment gateway confirmation.</p>
            </ComplianceBlock>
          </div>

          {/* 5. Contact Us & Grievance — full width card */}
          <div className="mt-12" data-testid="compliance-contact">
            <div className="eyebrow">05 · Contact</div>
            <h3 className="font-serif text-2xl md:text-3xl mt-2" style={{ color: "var(--ink)" }}>Contact Us &amp; Grievance</h3>
            <div className="mt-4 p-6 md:p-8 border" style={{ borderColor: "var(--gold)", background: "var(--bone)" }}>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-5 text-sm md:text-[15px]">
                <div className="flex flex-col">
                  <dt className="text-[10px] tracking-widest uppercase" style={{ color: "var(--ash)" }}>Platform Name</dt>
                  <dd className="font-serif text-lg md:text-xl" style={{ color: "var(--crimson)" }}>
                    Vaidik Shadi (वैदिक शादी)
                    <span className="block text-[11px] tracking-wide font-sans mt-1" style={{ color: "var(--ash)" }}>
                      A Brand of Vedika Sanskritik Bandhan
                    </span>
                  </dd>
                </div>
                <div className="flex flex-col">
                  <dt className="text-[10px] tracking-widest uppercase" style={{ color: "var(--ash)" }}>Official Website</dt>
                  <dd>
                    <a href="https://vaidikshadi.in" style={{ color: "var(--ink)", textDecoration: "underline" }} data-testid="compliance-website">
                      vaidikshadi.in
                    </a>
                  </dd>
                </div>
                <div className="flex flex-col">
                  <dt className="text-[10px] tracking-widest uppercase" style={{ color: "var(--ash)" }}>Proprietor</dt>
                  <dd style={{ color: "var(--ink)" }} data-testid="compliance-proprietor">Archna Kumari</dd>
                </div>
                <div className="flex flex-col">
                  <dt className="text-[10px] tracking-widest uppercase" style={{ color: "var(--ash)" }}>Authorized Representative</dt>
                  <dd style={{ color: "var(--ink)" }}>Vinay Kumar Giri</dd>
                </div>
                <div className="flex flex-col sm:col-span-2">
                  <dt className="text-[10px] tracking-widest uppercase" style={{ color: "var(--ash)" }}>Contact Support</dt>
                  <dd>
                    <a href="tel:+918708808649" style={{ color: "var(--crimson)" }} data-testid="compliance-phone">
                      +91 8708808649
                    </a>
                  </dd>
                </div>
                <div className="flex flex-col sm:col-span-2">
                  <dt className="text-[10px] tracking-widest uppercase" style={{ color: "var(--ash)" }}>Official Email</dt>
                  <dd>
                    <a href="mailto:vava.vaidikshadi.official@gmail.com" style={{ color: "var(--crimson)" }} data-testid="compliance-email">
                      vava.vaidikshadi.official@gmail.com
                    </a>
                  </dd>
                </div>
                <div className="flex flex-col sm:col-span-2">
                  <dt className="text-[10px] tracking-widest uppercase" style={{ color: "var(--ash)" }}>Official Address</dt>
                  <dd className="leading-relaxed" style={{ color: "var(--ink)" }} data-testid="compliance-address">
                    Flat No. 9, Village &amp; P.O. &amp; P.S.&nbsp;-&nbsp;Inarwa, Block&nbsp;-&nbsp;Mainatand, Paschim Champaran (West Champaran), Bihar&nbsp;-&nbsp;845306
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          <p className="mt-10 text-[10px] tracking-widest uppercase text-center" style={{ color: "var(--ash)" }}>
            By using vaidikshadi.in you agree to the terms above.
          </p>

          {/* Highlighted compliance disclaimers — Age + CSAM */}
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            <div data-testid="age-disclaimer" className="p-6 md:p-7"
              style={{ background: "var(--bone)", border: "1px solid var(--gold)" }}>
              <div className="flex items-center gap-2">
                <ShieldCheck size={16} style={{ color: "var(--crimson)" }}/>
                <div className="eyebrow">Age Restriction Disclaimer</div>
              </div>
              <p className="mt-3 text-sm md:text-[15px] leading-relaxed" style={{ color: "var(--ash)" }}>
                Our platform is strictly for legally marriageable individuals (aged <strong style={{ color: "var(--ink)" }}>18 and above for females</strong>, and <strong style={{ color: "var(--ink)" }}>21 and above for males</strong>). We do not knowingly collect, solicit, or maintain any personal data from individuals under the age of 18.
              </p>
            </div>

            <div data-testid="csam-disclaimer" className="p-6 md:p-7"
              style={{ background: "rgba(122, 14, 24, 0.06)", border: "1px solid var(--crimson)" }}>
              <div className="flex items-center gap-2">
                <Lock size={16} style={{ color: "var(--crimson)" }}/>
                <div className="eyebrow" style={{ color: "var(--crimson)" }}>Child Safety (CSAM) Policy</div>
              </div>
              <p className="mt-3 text-sm md:text-[15px] leading-relaxed" style={{ color: "var(--ash)" }}>
                We enforce a strict <strong style={{ color: "var(--ink)" }}>zero-tolerance policy</strong> against any form of Child Sexual Abuse Material (CSAM). Any user found sharing, uploading, or engaging in such restricted content will be permanently banned, and their details will be immediately reported to national law enforcement authorities. For any safety, policy, or abuse concerns, please contact us immediately at our official support email: <a href="mailto:support@vaidikshadi.in" style={{ color: "var(--crimson)", textDecoration: "underline" }} data-testid="csam-email">support@vaidikshadi.in</a>.
              </p>
            </div>
          </div>
        </div>
      </section>

      <div style={{ height: "32px" }} aria-hidden/>

      {/* Modal overlays — Step 1 shells + Step 2/3 live */}
      <GuestLeadModal open={modal === "guest"} onClose={closeModal}/>
      <AuthModal open={modal === "wizard"} onClose={closeModal}/>
    </div>
  );
}
